import React, { memo, useRef, useState, useEffect } from "react";
import { useDebouncedCallback } from "use-debounce";
import { Search, X } from "lucide-react";
import { geminiInput } from "../../services/genAI_autocomplete";

const ReportSearchBar = memo(
  ({
    searchQuery = "",
    setSearchQuery,
    size = "small",
    placeholder = "Search by party name or case number",
    enableSuggestions = false,
  }) => {
    const suggestionsRef = useRef([]);
    const containerRef = useRef(null);
    const [highlightIndex, setHighlightIndex] = useState(-1);
    const [, setHasGeminiFetched] = useState(false);

    const debouncedFetchSuggestions = useDebouncedCallback(async (query) => {
      if (!enableSuggestions) {
        return;
      }

      if (query.trim() !== "") {
        const response = await geminiInput(query);
        suggestionsRef.current = response;
        setHasGeminiFetched(true);
      }
    }, 300);

    const handleInput = (event) => {
      const newQuery = event.target.value;
      setSearchQuery(newQuery);
      setHighlightIndex(-1);

      if (!enableSuggestions) {
        return;
      }

      if (newQuery === "") {
        setHasGeminiFetched(false);
      } else {
        debouncedFetchSuggestions(newQuery);
      }
    };

    const handleReset = () => {
      setSearchQuery("");
      setHighlightIndex(-1);
    };

    const handleSuggestionClick = (suggestion) => {
      setSearchQuery(suggestion);
      setHighlightIndex(-1);
    };

    const handleKeyDown = (event) => {
      if (suggestionsRef.current.length === 0) {
        return;
      }

      if (event.key === "ArrowDown") {
        setHighlightIndex((prevIndex) =>
          prevIndex < suggestionsRef.current.length - 1 ? prevIndex + 1 : 0
        );
        event.preventDefault();
      } else if (event.key === "ArrowUp") {
        setHighlightIndex((prevIndex) =>
          prevIndex > 0 ? prevIndex - 1 : suggestionsRef.current.length - 1
        );
        event.preventDefault();
      } else if (event.key === "Enter") {
        if (
          highlightIndex >= 0 &&
          highlightIndex < suggestionsRef.current.length
        ) {
          handleSuggestionClick(suggestionsRef.current[highlightIndex]);
          event.preventDefault();
        }
      }
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setHighlightIndex(-1);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div
        ref={containerRef}
        className={`${
          size === "small"
            ? "justify-start max-w-[820px]"
            : "justify-center px-5 sm:px-10 max-w-[720px]"
        } items-center w-full`}
      >
        <div
          className={`relative z-[100] flex ${
            size === "small" ? "h-[42px]" : "h-[52px]"
          } items-center text-pl-text-primary bg-pl-bg-elevated border-[1.5px] border-pl-border rounded-pl-lg px-4 transition-all duration-150`}
        >
          <input
            type="search"
            value={searchQuery}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="bg-transparent ps-1 md:ps-2 py-2 pe-5 md:pe-9 w-full text-15 md:text-16 focus:outline-none flex-1 placeholder-pl-text-tertiary"
          />

          {(searchQuery || "").length > 0 && (
            <div
              onClick={handleReset}
              className="absolute right-16 top-1.5 md:top-2 cursor-pointer"
            >
              <X size={20} className="text-pl-text-secondary" strokeWidth={1.5} />
            </div>
          )}

          <div className="flex justify-center items-center me-2">
            <div className="h-6 border-l border-pl-border-strong me-3"></div>
            <Search
              size={20}
              strokeWidth={1.5}
              className="text-pl-text-tertiary"
            />
          </div>

          {searchQuery.trim() !== "" && suggestionsRef.current.length > 0 && (
            <div
              className={`absolute z-[100] w-full left-0 px-1 ${
                size === "small" ? "top-[54px]" : "top-[54px] md:top-16"
              }`}
            >
              <div className="z-[100] text-14 text-pl-text-primary bg-pl-bg-elevated rounded-pl-lg shadow-pl-md border border-pl-border overflow-hidden w-full">
                {suggestionsRef.current.map((suggestion, index) => (
                  <div
                    key={index}
                    className={`w-full px-3 md:px-4 py-2 md:py-3 text-left cursor-pointer ${
                      index === highlightIndex
                        ? "bg-pl-bg-subtle"
                        : "hover:bg-pl-bg-subtle"
                    }`}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

export default ReportSearchBar;
